import {
  TextField,
  ReferenceManyField,
  SingleFieldList,
  ChipField,
  TextInput,
  WithRecord,
  useGetList,
  Link,
} from "react-admin";
import { RecordSummary, MyShow } from "../ra-lb-tools/components/MyShow";
import { Box, Chip } from "@mui/material";
import { useParams } from "react-router-dom";
import { TowerList } from "./tower/towers";
import { MyList } from "../ra-lb-tools/components/myList";
import { LabeledColumn } from "../ra-lb-tools/components/labeledColumn";
import { Memory } from "@mui/icons-material";
import { UniqueReferenceManyField } from "../ra-lb-tools/components/uniqueReferenceManyField";
import { DeviceHistoryList } from "./deviceHistory";
import { ListCount } from "../ra-lb-tools/components/listCount";
import { MyEdit } from "../ra-lb-tools/components/myMutator";
import {
  MyTabbedShowLayout,
  Tab,
} from "../ra-lb-tools/components/myTabbedShowLayout";
import { MyDateField } from "../ra-lb-tools/components/myDateField";
import {
  MyReferenceField,
  MyReferenceInput,
} from "../ra-lb-tools/components/myReference";
import { DeviceAssemblyList } from "./deviceAssembly";

export const DeviceIcon = Memory;

const filters = [<TextInput source="serialNumber" alwaysOn key={1} />];

const getChipColor = (tower) => {
  if (tower.statusId == 4) {
    return "error";
  } else if (tower.isForTest) {
    return "testing";
  } else {
    return "default";
  }
};

const TowerCount = () => {
  return (
    <WithRecord
      render={(tower) =>
        tower._count > 1 ? (
          <Box className="TowerCount">
            <Box sx={{ fontWeight: "bold", margin: "5px 0" }}>
              Board Towers:
            </Box>
            <Link to={`/devices/${tower.deviceSerial}/show/towers-current`}>
              <Chip label={tower._count} color="error" clickable />
            </Link>
          </Box>
        ) : null
      }
    />
  );
};

export const DeviceCurrentTowersCount = (props) => (
  <ReferenceManyField
    className="DeviceCurrentTowers"
    source="serialNumber"
    reference="towers"
    target="device_serial"
    sortable={false}
    {...props}
  >
    <ListCount>
      <TowerCount />
    </ListCount>
  </ReferenceManyField>
);

export const DeviceCurrentTowers = (props) => {
  return (
    <ReferenceManyField
      className="DeviceCurrentTowers"
      source="serialNumber"
      reference="towers"
      target="device_serial"
      filter={{ statusId: 0 }}
      sortable={false}
      {...props}
    >
      <SingleFieldList linkType="show">
        <WithRecord
          render={(record) => (
            <ChipField source="name" color={getChipColor(record)} />
          )}
        />
      </SingleFieldList>
    </ReferenceManyField>
  );
};

export const DeviceHistoryTowers = () => (
  <UniqueReferenceManyField
    className="DeviceHistoryTowers"
    label="Tower history"
    source="serialNumber"
    reference="device-history"
    target="serial_number"
    uniqueField="towerId"
    sortable={false}
  >
    <ListCount>
      <WithRecord
        render={(device) => (
          <Link to={`/devices/${device.id}/show/history`}>
            <Chip label={device._count} clickable />
          </Link>
        )}
      />
    </ListCount>
  </UniqueReferenceManyField>
);

export const DeviceList = (props) => (
  <MyList {...props} resource="devices" filters={filters}>
    <TextField source="id" />
    <TextField source="serialNumber" />
    <TextField source="manufacturerSerialNumber" />
    <DeviceCurrentTowers label="Current tower" />
    <DeviceHistoryTowers label="Tower history" />
    <MyDateField source="createdAt" showTime />
    <MyDateField source="updatedAt" showTime />
  </MyList>
);

export const DeviceShow = (props) => {
  const id = useParams()["id"];

  if (isNaN(Number(id))) {
    return <DeviceShowBySerial {...props} />;
  } else {
    return <DeviceShowView {...props} />;
  }
};

export const DeviceShowBySerial = (props) => {
  const serial = useParams()["id"];

  const { data, isLoading } = useGetList("devices", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "DESC" },
    filter: { serial_number: serial },
  });

  if (isLoading) {
    return null;
  }

  const record = data[0];

  return <DeviceShowView id={record.id} {...props} />;
};

export const DeviceShowView = (props) => {
  return (
    <MyShow labelField="serialNumber" {...props}>
      <MyTabbedShowLayout>
        <Tab label="Summary" can={true}>
          <RecordSummary>
            <LabeledColumn>
              <TextField source="id" />
              <TextField source="serialNumber" />
              <TextField source="manufacturerSerialNumber" />
              <MyDateField source="createdAt" showTime />
              <MyDateField source="updatedAt" showTime />
            </LabeledColumn>
            <LabeledColumn>
              <DeviceCurrentTowers label="Current tower" />
              <DeviceHistoryTowers label="Tower history" />
              <TextField source="assembly.shield1MfgSerial" />
              <TextField source="assembly.shield2MfgSerial" />
              <TextField source="assembly.particleId" />
            </LabeledColumn>
          </RecordSummary>
        </Tab>
        <Tab
          label="Assemblies"
          path="assemblies"
          can={{ action: "list", resource: "device-assembly" }}
        >
          <WithRecord
            render={(device) => (
              <DeviceAssemblyList filter={{ device_id: device.id }} />
            )}
          />
        </Tab>
        <Tab
          label="Current Towers"
          path="towers-current"
          can={{ action: "list", resource: "towers" }}
        >
          <WithRecord
            render={(device) => (
              <TowerList
                filter={{ deviceSerial: device.serialNumber, statusId: 0 }}
              />
            )}
          />
        </Tab>
        <Tab
          label="History"
          path="history"
          can={{ action: "list", resource: "device-history" }}
        >
          <WithRecord
            render={(device) => (
              <DeviceHistoryList
                filter={{ serialNumber: device.serialNumber }}
              />
            )}
          />
        </Tab>
      </MyTabbedShowLayout>
    </MyShow>
  );
};

export const DeviceEdit = () => {
  return (
    <MyEdit resource="devices" labelField="serialNumber">
      <TextInput source="manufacturerSerialNumber" />
    </MyEdit>
  );
};

const referenceProps = {
  source: "deviceId",
  reference: "devices",
  primaryField: "serialNumber",
};

export const DeviceInput = (props) => {
  return <MyReferenceInput {...referenceProps} {...props} />;
};

export const DeviceField = (props) => {
  return <MyReferenceField {...referenceProps} {...props} />;
};
